/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { Table } from "@duxton-web/ui"
import dayjs from "dayjs"
import { jsx, css } from "@emotion/core"
import { ExtraReviewsFilterConfig, Review } from "types/api/menuFeedback.type"
import { ActionType, TableKeys } from "types/models/feedback"
import { AppState } from "store/store.type"
import { SorterConfig, SorterDirection } from "types/models/table"
import { useTranslationsMap } from "common/utils/i18n"

import { uniq } from "lodash"
import { BusinessType } from "types/models/menu"
import { ServiceType } from "constants/feedback"
import { getFeedbackDateRange } from "utils/feedback_helper"
import {
  RatingColumn,
  ReviewDetailsColumn,
  FromColumn,
  StoreBranchColumn,
  StatusColumn,
  ReviewActionColumn,
} from "./FeedbackColumns"
import { determineFeedbackRowAction } from "./utils"
import { FeedbackListStyle } from "./FeedbackList.styles"

export type FeedbackListProp = {
  onClick(actionType: ActionType, review: Review): void
  onFetchReviews?(extras?: ExtraReviewsFilterConfig): any
}

const FeedbackList: React.FC<FeedbackListProp> = ({
  onClick,
  onFetchReviews = () => {},
}) => {
  const {
    menu: { outlets },
    feedback,
    feedbackDrawer: { reviewThrottle },
  } = useSelector((state: AppState) => state)
  const cities = uniq(outlets?.map(o => o.city))
  const { reviews, loading } = feedback

  const textMap = useTranslationsMap({
    ratings: "feedback.tableHeadings.ratings",
    reviewDetail: "feedback.tableHeadings.review",
    customer: "feedback.tableHeadings.customer",
    store: "feedback.tableHeadings.store",
    type: "feedback.tableHeadings.type",
    date: "feedback.tableHeadings.date",
    more: "feedback.tableHeadings.more",
  })

  const renderRatingColumn = (record: Review) => (
    <RatingColumn rating={record.rating} />
  )
  const renderReviewDetailColumn = (record: Review) => (
    <ReviewDetailsColumn review={record} onClick={onClick} />
  )
  const renderFromColumn = (record: Review) => <FromColumn review={record} />
  const renderStoreBranchColumn = (record: Review) => (
    <StoreBranchColumn review={record} />
  )
  const renderStatusColumn = (record: Review) => (
    <StatusColumn review={record} />
  )
  const renderDateColumn = (record: Review) => (
    <p
      css={css({
        color: "#707070",
      })}
    >
      {`${dayjs(record.createdAt).format("DD MMM YYYY")}`}
      <br />
      {`${dayjs(record.createdAt).format("HH:mm")}`}
    </p>
  )
  const renderActionColumn = (record: Review) => (
    <ReviewActionColumn
      review={record}
      onClick={onClick}
      reviewThrottle={reviewThrottle}
    />
  )

  const onChangeTableState = (
    _pagination: any,
    _filters: any,
    sorter: SorterConfig,
  ) => {
    if (
      sorter.columnKey === TableKeys.REVIEW_DATE ||
      sorter.columnKey === TableKeys.RATINGS
    ) {
      onFetchReviews({
        orderBy: sorter.columnKey,
        asc: sorter.order === SorterDirection.ASC,
      })
    }
  }

  const onRowClick = (record: Review) => {
    return {
      onClick: () => {
        const drawerAction = determineFeedbackRowAction(record)
        onClick(drawerAction, record)
      },
    }
  }

  const defaultBusinessType = outlets?.[0]?.businessType

  useEffect(() => {
    const { startDate, endDate } = getFeedbackDateRange()

    onFetchReviews({
      cityName: cities?.[0],
      businessType: defaultBusinessType ?? BusinessType.Food,
      serviceType: ServiceType.DELIVERY,
      startDate,
      endDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Table
      dataSource={reviews ?? []}
      rowKey="reviewID"
      loading={loading}
      pagination={false}
      onChange={onChangeTableState}
      onRow={onRowClick}
      css={FeedbackListStyle}
    >
      <Table.Column
        width={120}
        key={TableKeys.RATINGS}
        title={textMap.ratings}
        sortDirections={[SorterDirection.ASC, SorterDirection.DESC]}
        sorter={() => 0}
        render={renderRatingColumn}
      />
      <Table.Column
        key={TableKeys.REVIEW_DETAILS}
        title={textMap.reviewDetail}
        render={renderReviewDetailColumn}
      />
      <Table.Column
        key={TableKeys.FROM}
        title={textMap.customer}
        render={renderFromColumn}
      />
      <Table.Column
        key={TableKeys.STORE_BRANCH}
        title={textMap.store}
        render={renderStoreBranchColumn}
      />
      <Table.Column
        key={TableKeys.STATUS}
        title={textMap.type}
        render={renderStatusColumn}
      />
      <Table.Column
        key={TableKeys.REVIEW_DATE}
        title={textMap.date}
        render={renderDateColumn}
        sortDirections={[SorterDirection.ASC, SorterDirection.DESC]}
        sorter={() => 0}
      />
      <Table.Column
        key={TableKeys.ACTIONS}
        title={textMap.more}
        render={renderActionColumn}
      />
    </Table>
  )
}

export default FeedbackList
